import React, { useRef } from 'react';
import { parseAsciiTxt } from '../utils/asciiImporter';
import { useLanguage } from '../context/LanguageProvider';
import { useThemeContext } from '../context/ThemeProvider';
import { devLog } from '../utils/devLog';
import ActionButton from './common/ActionButton';
import type { AsciiImportButtonProps, AsciiImportSettings, TimeSignature, NoteDivision, DrumGridData } from '../types';

const AsciiImportButton: React.FC<AsciiImportButtonProps> = ({ 
  onImport, 
  currentInstruments = [], 
  disabled = false, 
  onError, 
  onSettingsUpdate 
}): JSX.Element => {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const { t } = useLanguage();
  const { getClass } = useThemeContext();

  // 解析檔名以提取設定參數
  const parseFilename = (filename: string): AsciiImportSettings | null => {
    // 檔名格式: ascii-output-[time-signature]-[note division]-[tempo]-[datetime].txt
    const match = filename.match(/^ascii-output-([^-]+)-([^-]+)-(\d+)-(.+)\.txt$/);
    if (match) {
      const [, timeSignature, noteDivision, tempo, datetime] = match;
      
      // 驗證參數是否有效
      const validTimeSignatures = ['4-4', '3-4', '6-8']; // 對應 4/4, 3/4, 6/8
      const validNoteDivisions = ['4n', '8n', '16n'];
      const tempoNum = parseInt(tempo);
      
      const settings: AsciiImportSettings = {};
      
      if (validTimeSignatures.includes(timeSignature)) {
        settings.timeSignature = timeSignature.replace('-', '/') as TimeSignature; // 4-4 -> 4/4
      }
      
      if (validNoteDivisions.includes(noteDivision)) {
        settings.noteDivision = noteDivision as NoteDivision;
      }
      
      if (tempoNum >= 10 && tempoNum <= 600) {
        settings.tempo = tempoNum;
      }
      
      return Object.keys(settings).length > 0 ? settings : null;
    }
    
    return null;
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (disabled) {
      devLog('🚫 ASCII Import button disabled, ignoring file input');
      return;
    }

    const file = e.target.files?.[0];
    if (!file) return;

    // 嘗試從檔名提取設定
    const extractedSettings = parseFilename(file.name);
    if (extractedSettings) {
      devLog('🎵 ASCII Import: Extracted settings from filename:', extractedSettings);
    }

    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      try {
        const result = event.target?.result;
        if (typeof result !== 'string') return;
        
        const { grid, drumKitId } = parseAsciiTxt(result, currentInstruments) as { grid: DrumGridData; drumKitId: string | null };
        
        // 傳遞grid、drumKitId和從檔名提取的設定
        onImport?.({ 
          grid, 
          drumKitId: drumKitId || undefined, 
          settings: extractedSettings || undefined
        });
      } catch (error) {
        console.error('Error parsing ASCII file:', error);
        if (onError) {
          onError(t('asciiImportError'));
        }
      }
    };

    reader.readAsText(file);
    // reset input to allow selecting the same file again
    e.target.value = '';
  };

  return (
    <>
      <ActionButton
        onClick={() => {
          if (!disabled) {
            fileInput.current?.click();
          }
        }}
        disabled={disabled}
        loading={disabled}
        icon="📥"
        text={disabled ? (t('importing') || '匯入中...') : t('import')}
        tooltip={disabled ? (t('importing') || '匯入中...') : t('import')}
        variant="primary"
      />
      <input
        ref={fileInput}
        type="file"
        accept=".txt"
        style={{ display: 'none' }}
        onChange={handleFile}
      />
    </>
  );
};

export default AsciiImportButton; 